import { type PropsWithChildren, useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { theme } from '@garnish/constants';
import { BodyText, Switch, VStack } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────

export const NotificationPreferencesControlsContainer = (
  props: PropsWithChildren,
) => {
  return (
    <VStack
      gap={0}
      hasDivider={true}
      dividerColor={theme.colors.OPACITY.BLACK.LIGHTEST}
      style={styles.container}
    >
      {props.children}
    </VStack>
  );
};

export const NotificationPreferencesControlSection = (
  props: PropsWithChildren,
) => {
  return <View style={styles.section}>{props.children}</View>;
};

// ─── Child Components ────────────────────────────────────────────────

export const NotificationsPreferencesControlTitle = (
  props: PropsWithChildren,
) => {
  return (
    <View style={styles.controlTitleContainer}>
      <BodyText sizeMatch={['14']}>{props.children}</BodyText>
    </View>
  );
};

export const NotificationsPreferencesControlToggle = (
  props: NotificationsPreferencesControlToggleProps,
) => {
  const {
    onChange,
    value,
    disabled = false,
    category,
    subscriptionType,
  } = props;

  const handleOnChange = useCallback(
    (updatedValue: boolean) => {
      onChange({
        category,
        subscriptionType,
        value: updatedValue,
      });
    },
    [category, onChange, subscriptionType],
  );

  return (
    <Switch
      size="medium"
      value={value}
      showIcons={false}
      onChange={handleOnChange}
      disabled={disabled}
    />
  );
};

// ─── Styles ──────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingTop: theme.spacing['4'],
  },
  section: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  controlTitleContainer: {
    paddingVertical: theme.spacing['5'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────

type NotificationsPreferencesControlToggleProps = ToggleState & {
  value: boolean;
  disabled?: boolean;
  onChange: (value: ToggleState) => void;
};

type ToggleState = {
  category: string;
  subscriptionType: string;
  value: boolean;
};
